import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

export interface FizboConversion {
  kabul: number;
  dusunecek: number;
  red: number;
  baska: number;
}

export interface MonthlyFizbo {
  month: string;
  count: number;
  kabul: number;
}

export interface PortfolioSaleTime {
  type: string;
  avgDays: number;
}

export interface MonthlyRevenue {
  month: string;
  revenue: number;
}

export interface UnsoldLoss {
  monthlyInterestLoss: number;
  monthlyInflationLoss: number;
  totalPortfolioValue: number;
  avgDaysUnsold: number;
}

export interface ReportStats {
  fizboConversion: FizboConversion;
  monthlyFizbo: MonthlyFizbo[];
  portfolioSaleTime: PortfolioSaleTime[];
  monthlyRevenue: MonthlyRevenue[];
  unsoldLoss: UnsoldLoss;
}

const MONTH_NAMES = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

function monthLabel(d: Date): string {
  return MONTH_NAMES[d.getMonth()];
}

export function useReportStats() {
  const { user } = useAuth();
  const [stats, setStats] = useState<ReportStats>({
    fizboConversion: { kabul: 0, dusunecek: 0, red: 0, baska: 0 },
    monthlyFizbo: [],
    portfolioSaleTime: [],
    monthlyRevenue: [],
    unsoldLoss: {
      monthlyInterestLoss: 0,
      monthlyInflationLoss: 0,
      totalPortfolioValue: 0,
      avgDaysUnsold: 0,
    },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    setError(null);

    const { data: fizboData, error: fizboErr } = await supabase
      .from('ep_fizbo_listings')
      .select('status, created_at, kanban_stage, price')
      .eq('user_id', user.id);

    const { data: portfolioData, error: portfolioErr } = await supabase
      .from('ep_portfolios')
      .select('property_type, status, price, created_at')
      .eq('user_id', user.id);

    if (fizboErr || portfolioErr) {
      setError(fizboErr?.message || portfolioErr?.message || 'Veri yüklenirken hata oluştu');
      setIsLoading(false);
      return;
    }

    const fizboRows = (fizboData || []) as {
      status: string | null;
      created_at: string;
      kanban_stage: string | null;
      price: number | null;
    }[];

    const portfolioRows = (portfolioData || []) as {
      property_type: string | null;
      status: string | null;
      price: number | null;
      created_at: string;
    }[];

    const conversion: FizboConversion = { kabul: 0, dusunecek: 0, red: 0, baska: 0 };
    fizboRows.forEach((f) => {
      const s = f.status || '';
      if (s === 'Kabul') conversion.kabul += 1;
      else if (s === 'Düşünecek') conversion.dusunecek += 1;
      else if (s === 'Red') conversion.red += 1;
      else if (s === 'Başka Emlakçıda') conversion.baska += 1;
    });

    const monthlyMap = new Map<string, { count: number; kabul: number }>();
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      monthlyMap.set(monthLabel(d), { count: 0, kabul: 0 });
    }

    fizboRows.forEach((f) => {
      const d = new Date(f.created_at);
      const label = monthLabel(d);
      const existing = monthlyMap.get(label);
      if (!existing) return;
      existing.count += 1;
      if (f.status === 'Kabul') existing.kabul += 1;
    });

    const monthlyFizbo: MonthlyFizbo[] = MONTH_NAMES.map((m) => {
      const d = monthlyMap.get(m) || { count: 0, kabul: 0 };
      return { month: m, count: d.count, kabul: d.kabul };
    });

    const propTypeMap = new Map<string, { count: number; totalDays: number }>();
    portfolioRows.forEach((p) => {
      const type = p.property_type || 'Diğer';
      if (!propTypeMap.has(type)) {
        propTypeMap.set(type, { count: 0, totalDays: 0 });
      }
      const e = propTypeMap.get(type)!;
      e.count += 1;
      const days = Math.floor(
        (now.getTime() - new Date(p.created_at).getTime()) / (1000 * 60 * 60 * 24)
      );
      e.totalDays += Math.max(0, days);
    });

    const portfolioSaleTime: PortfolioSaleTime[] = Array.from(propTypeMap.entries()).map(
      ([type, vals]) => ({
        type,
        avgDays: vals.count > 0 ? Math.round(vals.totalDays / vals.count) : 0,
      })
    );

    const revenueMap = new Map<string, number>();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      revenueMap.set(monthLabel(d), 0);
    }

    fizboRows.forEach((f) => {
      if (f.kanban_stage === 'Kapandı') {
        const d = new Date(f.created_at);
        const label = monthLabel(d);
        const existing = revenueMap.get(label);
        if (existing !== undefined) {
          revenueMap.set(label, existing + (Number(f.price) || 0) * 0.03);
        }
      }
    });

    const monthlyRevenue: MonthlyRevenue[] = MONTH_NAMES.map((m) => ({
      month: m,
      revenue: revenueMap.get(m) || 0,
    }));

    const activePortfolios = portfolioRows.filter((p) => p.status === 'Aktif');
    const totalPortfolioValue = activePortfolios.reduce((s, p) => s + (Number(p.price) || 0), 0);
    const avgDaysUnsold =
      activePortfolios.length > 0
        ? Math.round(
            activePortfolios.reduce((sum, p) => {
              const days = Math.floor(
                (now.getTime() - new Date(p.created_at).getTime()) / (1000 * 60 * 60 * 24)
              );
              return sum + Math.max(0, days);
            }, 0) / activePortfolios.length
          )
        : 0;

    const monthlyInterestLoss = Math.round(totalPortfolioValue * 0.01);
    const monthlyInflationLoss = Math.round(totalPortfolioValue * 0.0067);

    setStats({
      fizboConversion: conversion,
      monthlyFizbo,
      portfolioSaleTime,
      monthlyRevenue,
      unsoldLoss: {
        monthlyInterestLoss,
        monthlyInflationLoss,
        totalPortfolioValue,
        avgDaysUnsold,
      },
    });
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, isLoading, error, refresh: fetchStats };
}